// ┌─────────────────────────────────────────────────────────────────┐
// │                        PRIMARY STORAGE                          │
// │                                                                 │
// │   OPFS (Origin Private File System) + SQLite                    │
// │   - Fast, synchronous file access via Web Worker                │
// │   - Works on ALL browsers (iOS Safari, Chrome, Firefox)         │
// │   - But: Can be evicted by iOS under storage pressure           │
// └─────────────────────────────────────────────────────────────────┘
//                               │
//                               │ Backup
//                               ▼
// ┌─────────────────────────────────────────────────────────────────┐
// │                      BACKUP OPTIONS                             │
// │                                                                 │
// │  Option A: Directory Picker (Chrome/Edge desktop only)          │
// │  ├─ User picks a folder once                                    │
// │  ├─ Auto-backup on interval when dirty                          │
// │  └─ Auto-restore if OPFS is missing                             │
// │                                                                 │
// │  Option B: Download/Upload (ALL browsers including iOS)         │
// │  ├─ downloadBackup() → triggers .sqlite3 file download          │
// │  ├─ uploadRestore(file) → restore from file input               │
// │  └─ promptAndRestore() → opens file picker + restores           │
// └─────────────────────────────────────────────────────────────────┘
//
// Browser Support Matrix (12.2025)
//
// Feature	        iOS Safari	 Chrome Android  Desktop   Chrome Firefox
// OPFS + SQLite	✅	        ✅	            ✅	     ✅
// Directory Picker	❌	        ✅ (v132+)	    ✅	     ❌
// Download/Upload	✅	        ✅               ✅	     ✅
//

import type { default as Aeppic } from '../aeppic.js';
import { createOfflineStoresAsync, type OfflineStoresAsync } from './stores-async.js';
import { OfflineWorkerClient } from './worker/client.js';
import { OfflineDocuments } from './documents.js';

// Re-export for external use
export { OfflineDocuments } from './documents.js';

const BACKUP_CHUNK_SIZE = 1024 * 1024; // 1MB chunks for streaming
const INDEXEDDB_HANDLE_STORE = 'aeppic-offline-handles';
const INDEXEDDB_HANDLE_KEY = 'directory-handle';

/**
 * Browser capability flags for offline storage features.
 */
export interface OfflineCapabilities {
    /** Whether OPFS (Origin Private File System) is supported */
    opfs: boolean;
    /** Whether Web Workers are supported (required for SQLite) */
    webWorkers: boolean;
    /** Whether File System Access API (showDirectoryPicker) is supported - for backup to user directory */
    fileSystemAccess: boolean;
    /** Whether IndexedDB is supported (for storing directory handles) */
    indexedDB: boolean;
    /** Whether all required features for basic offline are available */
    canUseOffline: boolean;
    /** Whether backup to user-selected directory is possible */
    canBackupToUserDirectory: boolean;
}

export class Offline {
    private _isAvailable: boolean | null = null;
    private _options: any = {};
    private _isPrepared: boolean = false;
    private _stores: OfflineStoresAsync | null = null;
    private _workerClient: OfflineWorkerClient | null = null;
    private _directoryHandle: FileSystemDirectoryHandle | null = null;
    private _backupInterval: number | null = null;
    private _dbPrefix: string = 'aeppic-offline';
    
    // High-level APIs
    private _documents: OfflineDocuments | null = null;

    constructor(private aeppic: Aeppic) {
        this._isAvailable = this.aeppic.Features.isEnabled('offline')
        this._isAvailable &&= 'serviceWorker' in navigator

        if (this._isAvailable) {
            console.log('Offline support is enabled.')
            this._options = this.aeppic.Features.getOptionsFor('offline') 
        }
    }

    /**
     * Check browser capabilities for offline storage features.
     * Use this to determine what features are available before showing UI.
     */
    static getCapabilities(): OfflineCapabilities {
        const opfs = 'storage' in navigator && 'getDirectory' in (navigator.storage || {});
        const webWorkers = 'Worker' in globalThis;
        const fileSystemAccess = 'showDirectoryPicker' in (globalThis.window || {});
        const indexedDB = 'indexedDB' in globalThis;
        
        return {
            opfs,
            webWorkers,
            fileSystemAccess,
            indexedDB,
            canUseOffline: opfs && webWorkers,
            canBackupToUserDirectory: fileSystemAccess && indexedDB
        };
    }

    get isAvailable(): boolean {
        return this._isAvailable;   
    }

    get isPrepared(): boolean {
        return this._isPrepared;
    }

    /** Whether a user directory is configured for persistent backup */
    get hasUserDirectory(): boolean {
        return this._directoryHandle !== null;
    }

    /** 
     * Check if backup to user directory is supported on this browser.
     * Returns false on iOS Safari, Firefox, etc.
     */
    get canBackupToUserDirectory(): boolean {
        return 'showDirectoryPicker' in (globalThis.window || {});
    }

    // ========================================================================
    // High-Level APIs
    // ========================================================================

    /**
     * High-level API for offline document storage.
     * Works with Types.Document objects directly.
     * 
     * @example
     * ```typescript
     * // Save a document
     * await aeppic.Offline.Documents.save(myDocument);
     * 
     * // Get a document
     * const doc = await aeppic.Offline.Documents.get('doc-123');
     * 
     * // Get children of a parent
     * const children = await aeppic.Offline.Documents.getChildren('/root/folder');
     * 
     * // Find by form
     * const invoices = await aeppic.Offline.Documents.findByForm('invoice-form');
     * ```
     */
    get Documents(): OfflineDocuments {
        if (!this._documents) {
            throw new Error('Offline: Not prepared. Call prepare() first.');
        }
        return this._documents;
    }

    // ========================================================================
    // Low-Level Store Access (for advanced use)
    // ========================================================================

    /** Access to the low-level documents store */
    get documents() {
        if (!this._stores) {
            throw new Error('Offline: Not prepared. Call prepare() first.');
        }
        return this._stores.documents;
    }

    /** Access to the low-level forms store */
    get forms() {
        if (!this._stores) {
            throw new Error('Offline: Not prepared. Call prepare() first.');
        }
        return this._stores.forms;
    }

    /** Access to the low-level changes store */
    get changes() {
        if (!this._stores) {
            throw new Error('Offline: Not prepared. Call prepare() first.');
        }
        return this._stores.changes;
    }

    /**
     * Request access to a user-selected directory for persistent backup storage.
     * 
     * NOTE: This feature is NOT available on:
     * 
     * - iOS Safari (no File System Access API)
     * - Firefox (not implemented)
     * - Older browsers
     * 
     * Use `canBackupToUserDirectory` to check availability before calling.
     * 
     * @returns true if directory was selected, false if cancelled or unsupported
     */
    async requestUserDirectory(): Promise<boolean> {
        if (!this.canBackupToUserDirectory) {
            console.warn('Offline: File System Access API not supported on this browser');
            return false;
        }

        try {
            const pickerOptions = this._options.directoryPicker || {};
            this._directoryHandle = await (window as any).showDirectoryPicker({
                id: pickerOptions.id || 'aeppic-offline-storage',
                mode: 'readwrite',
                startIn: pickerOptions.startIn || 'documents',
            });

            // Store the handle for future sessions
            await this._storeHandleInIndexedDB(this._directoryHandle);
            
            console.log('Offline: User directory selected:', this._directoryHandle.name);
            return true;
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log('Offline: User cancelled directory selection');
            } else {
                console.error('Offline: Failed to get directory handle:', error);
            }
            return false;
        }
    }

    /**
     * Try to restore a previously stored directory handle.
     */
    async restoreUserDirectory(): Promise<boolean> {
        try {
            const handle = await this._retrieveHandleFromIndexedDB();
            if (!handle) {
                return false;
            }

            // Verify we still have permission
            // Use any cast - queryPermission/requestPermission are part of File System Access API
            const permission = await (handle as any).queryPermission({ mode: 'readwrite' });
            if (permission === 'granted') {
                this._directoryHandle = handle;
                console.log('Offline: Restored user directory:', handle.name);
                return true;
            }

            // Try to request permission
            const newPermission = await (handle as any).requestPermission({ mode: 'readwrite' });
            if (newPermission === 'granted') {
                this._directoryHandle = handle;
                console.log('Offline: Re-granted user directory:', handle.name);
                return true;
            }

            console.log('Offline: Permission denied for stored directory');
            return false;
        } catch (error) {
            console.error('Offline: Failed to restore directory handle:', error);
            return false;
        }
    }

    private async _storeHandleInIndexedDB(handle: FileSystemDirectoryHandle): Promise<void> {
        const dbName = this._options.indexedDbForHandle || INDEXEDDB_HANDLE_STORE;
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, 1);
            
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains('handles')) {
                    db.createObjectStore('handles');
                }
            };
            
            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction('handles', 'readwrite');
                const store = tx.objectStore('handles');
                store.put(handle, INDEXEDDB_HANDLE_KEY);
                tx.oncomplete = () => {
                    db.close();
                    resolve();
                };
                tx.onerror = () => {
                    db.close();
                    reject(tx.error);
                };
            };
            
            request.onerror = () => reject(request.error);
        });
    }

    private async _retrieveHandleFromIndexedDB(): Promise<FileSystemDirectoryHandle | null> {
        const dbName = this._options.indexedDbForHandle || INDEXEDDB_HANDLE_STORE;
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, 1);
            
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains('handles')) {
                    db.createObjectStore('handles');
                }
            };
            
            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction('handles', 'readonly');
                const store = tx.objectStore('handles');
                const getRequest = store.get(INDEXEDDB_HANDLE_KEY);
                
                getRequest.onsuccess = () => {
                    db.close();
                    resolve(getRequest.result || null);
                };
                getRequest.onerror = () => {
                    db.close();
                    reject(getRequest.error);
                };
            };
            
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Force a backup to user directory now.
     * Can be called from UI to manually trigger a backup.
     * 
     * @returns Object with success status and details
     */
    async backupNow(): Promise<{ success: boolean; message: string; bytesWritten?: number }> {
        if (!this._workerClient) {
            return { success: false, message: 'Offline storage not initialized' };
        }

        if (!this._directoryHandle) {
            return { success: false, message: 'No backup directory configured. Call requestUserDirectory() first.' };
        }

        try {
            // Check if there are changes to backup
            const isDirty = await this._workerClient.checkDirty();
            if (!isDirty) {
                return { success: true, message: 'No changes to backup' };
            }

            // Prepare backup snapshot in OPFS
            const prepResult = await this._workerClient.prepareBackup();
            if (!prepResult.success) {
                const reason = (prepResult as { reason: string }).reason;
                return { success: false, message: `Failed to prepare backup: ${reason}` };
            }

            // Get backup size
            const backupSize = await this._workerClient.getBackupSize();
            console.log(`Offline: Starting backup (${(backupSize / 1024 / 1024).toFixed(2)} MB)`);

            // Get/create file in user directory
            const filename = `${this._dbPrefix}.sqlite3`;
            const fileHandle = await this._directoryHandle.getFileHandle(filename, { create: true });
            // createWritable is part of File System Access API
            const writable = await (fileHandle as any).createWritable();

            // Stream chunks from OPFS to user directory
            let offset = 0;
            while (true) {
                const chunk = await this._workerClient.streamBackupChunk(offset, BACKUP_CHUNK_SIZE);
                await writable.write(chunk.data);
                offset += chunk.bytesRead;
                
                if (chunk.done) {
                    break;
                }
            }

            await writable.close();

            // Cleanup backup file in OPFS
            await this._workerClient.cleanupBackup();

            console.log('Offline: Backup complete');
            return { 
                success: true, 
                message: `Backup complete (${(offset / 1024 / 1024).toFixed(2)} MB)`,
                bytesWritten: offset 
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('Offline: Backup failed:', error);
            return { success: false, message: `Backup failed: ${message}` };
        }
    }

    /**
     * Check if there are pending changes that need to be backed up.
     */
    async hasPendingChanges(): Promise<boolean> {
        if (!this._workerClient) {
            return false;
        }
        return this._workerClient.checkDirty();
    }

    /**
     * Download the database as a file (works on iOS Safari and all browsers).
     * This triggers a browser download of the SQLite database file.
     * 
     * Use this on iOS where showDirectoryPicker is not available.
     * 
     * @param filename - Optional custom filename (default: 'aeppic-offline-backup.sqlite3')
     * @returns Object with success status and details
     */
    async downloadBackup(filename?: string): Promise<{ success: boolean; message: string; size?: number }> {
        if (!this._workerClient) {
            return { success: false, message: 'Offline storage not initialized' };
        }

        try {
            // Prepare backup snapshot in OPFS
            const prepResult = await this._workerClient.prepareBackup();
            if (!prepResult.success) {
                const reason = (prepResult as { reason: string }).reason;
                if (reason === 'No changes to backup') {
                    // Still allow download even if no new changes
                }
            }

            // Get backup size
            const backupSize = await this._workerClient.getBackupSize();
            
            // Collect all chunks into a single blob
            const chunks: ArrayBuffer[] = [];
            let offset = 0;
            
            while (true) {
                const chunk = await this._workerClient.streamBackupChunk(offset, BACKUP_CHUNK_SIZE);
                chunks.push(chunk.data);
                offset += chunk.bytesRead;
                
                if (chunk.done) {
                    break;
                }
            }

            // Create blob from chunks
            const blob = new Blob(chunks, { type: 'application/x-sqlite3' });
            
            // Trigger download
            const downloadFilename = filename || `${this._dbPrefix}-backup.sqlite3`;
            const url = URL.createObjectURL(blob);
            
            const a = document.createElement('a');
            a.href = url;
            a.download = downloadFilename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Cleanup
            URL.revokeObjectURL(url);
            await this._workerClient.cleanupBackup();

            console.log(`Offline: Download started (${(backupSize / 1024 / 1024).toFixed(2)} MB)`);
            return { 
                success: true, 
                message: `Download started (${(backupSize / 1024 / 1024).toFixed(2)} MB)`,
                size: backupSize 
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('Offline: Download backup failed:', error);
            return { success: false, message: `Download failed: ${message}` };
        }
    }

    /**
     * Restore database from an uploaded file (works on iOS Safari and all browsers).
     * Pass a File object from an <input type="file"> element.
     * 
     * Use this on iOS where showDirectoryPicker is not available.
     * 
     * @param file - File object from file input
     * @returns Object with success status and details
     * 
     * @example
     * ```html
     * <input type="file" accept=".sqlite3" id="restore-input">
     * ```
     * ```typescript
     * const input = document.getElementById('restore-input') as HTMLInputElement;
     * input.onchange = async () => {
     *     const file = input.files?.[0];
     *     if (file) {
     *         const result = await aeppic.Offline.uploadRestore(file);
     *         console.log(result.message);
     *     }
     * };
     * ```
     */
    async uploadRestore(file: File): Promise<{ success: boolean; message: string }> {
        if (!this._workerClient) {
            return { success: false, message: 'Offline storage not initialized' };
        }

        if (!file) {
            return { success: false, message: 'No file provided' };
        }

        try {
            console.log(`Offline: Restoring from uploaded file (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

            // Start restore in worker
            await this._workerClient.startRestore();

            // Stream chunks from the file to OPFS
            const reader = file.stream().getReader();
            let bytesWritten = 0;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                bytesWritten += await this._workerClient.writeRestoreChunk(value.buffer);
            }

            // Finish restore and reopen database
            await this._workerClient.finishRestore();

            console.log('Offline: Restore from upload complete');
            return { 
                success: true, 
                message: `Restore complete (${(bytesWritten / 1024 / 1024).toFixed(2)} MB)` 
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('Offline: Restore from upload failed:', error);
            return { success: false, message: `Restore failed: ${message}` };
        }
    }

    /**
     * Open a file picker dialog and restore from selected backup file.
     * This is a convenience method that creates a file input internally.
     * 
     * Works on all browsers including iOS Safari.
     * 
     * @returns Promise that resolves when restore is complete or user cancels
     */
    async promptAndRestore(): Promise<{ success: boolean; message: string }> {
        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.sqlite3,.db';
            input.style.display = 'none';
            
            input.onchange = async () => {
                const file = input.files?.[0];
                document.body.removeChild(input);
                
                if (!file) {
                    resolve({ success: false, message: 'No file selected' });
                    return;
                }
                
                const result = await this.uploadRestore(file);
                resolve(result);
            };
            
            input.oncancel = () => {
                document.body.removeChild(input);
                resolve({ success: false, message: 'Cancelled' });
            };
            
            document.body.appendChild(input);
            input.click();
        });
    }

    /**
     * Restore database from user directory backup.
     */
    async restoreFromBackup(): Promise<boolean> {
        if (!this._directoryHandle || !this._workerClient) {
            console.warn('Offline: Cannot restore - no directory handle or worker');
            return false;
        }

        try {
            const filename = `${this._dbPrefix}.sqlite3`;
            
            // Check if backup file exists
            let fileHandle: FileSystemFileHandle;
            try {
                fileHandle = await this._directoryHandle.getFileHandle(filename);
            } catch {
                console.log('Offline: No backup file found');
                return false;
            }

            const file = await fileHandle.getFile();
            console.log(`Offline: Restoring from backup (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

            // Start restore in worker
            await this._workerClient.startRestore();

            // Stream chunks from user directory to OPFS
            const reader = file.stream().getReader();
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                await this._workerClient.writeRestoreChunk(value.buffer);
            }

            // Finish restore and reopen database
            await this._workerClient.finishRestore();

            console.log('Offline: Restore complete');
            return true;
        } catch (error) {
            console.error('Offline: Restore failed:', error);
            return false;
        }
    }

    /**
     * Start automatic backup interval.
     */
    private _startBackupInterval(): void {
        const intervalMs = this._options.storage?.backupIntervalMs || 60000;
        
        if (this._backupInterval) {
            clearInterval(this._backupInterval);
        }

        this._backupInterval = window.setInterval(async () => {
            if (this._directoryHandle && this._workerClient) {
                const result = await this.backupNow();
                if (!result.success && result.message !== 'No changes to backup') {
                    console.warn('Offline: Interval backup failed:', result.message);
                }
            }
        }, intervalMs);

        // Also backup on page unload
        window.addEventListener('beforeunload', () => {
            // Can't await here, but we try our best
            this.backupNow().catch(() => {});
        });

        console.log(`Offline: Backup interval started (${intervalMs}ms)`);
    }

    /**
     * Persist is not needed with worker-based OPFS storage.
     * The database is automatically persisted by SQLite's WAL mode.
     * This method triggers a backup to user directory if configured.
     */
    async persist(): Promise<void> {
        if (this._directoryHandle) {
            await this.backupNow();
        }
    }

    async deleteAllOfflineData(): Promise<void> {
        // Stop backup interval
        if (this._backupInterval) {
            clearInterval(this._backupInterval);
            this._backupInterval = null;
        }

        // Close and delete data from worker
        if (this._stores) {
            await this._stores.deleteAll();
            await this._stores.close();
            this._stores = null;
        }

        this._workerClient = null;
        this._isPrepared = false;
    }

    async prepare(): Promise<void> {
        if (!this.isAvailable) {
            throw new Error('Offline: Offline feature is not available.');
        }

        if (this.isPrepared) {
            return;
        }

        // Try to restore user directory handle
        await this.restoreUserDirectory();

        await this._buildDatabase();

        // Start backup interval if we have a user directory
        if (this._directoryHandle) {
            this._startBackupInterval();
        }

        this._isPrepared = true;
    }
    
    /**
     * Initialize the worker and SQLite database.
     */
    private async _buildDatabase(): Promise<void> {
        const workerPath = this._options.offlineStoreWorkerPath;
        const sqliteImportPath = this._options.sqliteImportPath;

        if (!workerPath) {
            throw new Error('Offline: offlineStoreWorkerPath is not configured in feature options.');
        }

        if (!sqliteImportPath) {
            throw new Error('Offline: sqliteImportPath is not configured in feature options.');
        }

        this._dbPrefix = this._options.offlineDbFileNamePrefix || 'aeppic-offline';
        
        // createOfflineStoresAsync initializes the worker and returns needsRestore
        const { stores, workerClient, needsRestore } = await createOfflineStoresAsync(workerPath, sqliteImportPath, this._dbPrefix);
        this._stores = stores;
        this._workerClient = workerClient;
        
        // Initialize high-level APIs
        this._documents = new OfflineDocuments(stores.documents);
        
        // Restore from backup if OPFS database was missing
        if (needsRestore && this._directoryHandle) {
            console.log('Offline: OPFS database not found, attempting restore from backup...');
            await this.restoreFromBackup();
        }
    }
}